/**
 * useThrottledSetAttributes — les attributs du participant local de la salle LiveKit, posés par un
 * seul passage étranglé (2 s, en tête et en queue), partagé par tous les écrans qui en posent.
 *
 * Déplacé tel quel de `common/hooks/livekit/` dans la partie commune du live (rien n'y est propre à
 * l'élève) : mêmes appels, mêmes délais ; seuls des types sont ajoutés.
 */
import { useConnectionState, useRoomContext } from "@livekit/components-react"
import { ConnectionState } from "livekit-client"
import throttle from "lodash/throttle"
import { useEffect } from "react"

type AttributesParticipant = { setAttributes: (attributes: Record<string, string>) => Promise<void> }

let currentLocalParticipant: AttributesParticipant | null = null
let isRoomConnected = false
let pendingAttrs: Record<string, string> | null = null
let pendingHandlers: { resolve: () => void; reject: (error: unknown) => void }[] = []
let isExecuting = false
const DEFAULT_INTERVAL = 2000
let manualRetryTimer: ReturnType<typeof setTimeout> | null = null

// Core flush logic: runs on leading and/or trailing edge
function executeFlush() {
    if (isExecuting) return
    const attrsToSet = pendingAttrs
    if (!attrsToSet) return
    // If not connected yet, retry after interval
    if (!isRoomConnected) {
        if (!manualRetryTimer) {
            manualRetryTimer = setTimeout(() => {
                manualRetryTimer = null
                executeFlush()
            }, DEFAULT_INTERVAL)
        }
        return
    }
    // Connected: clear any pending retry timer
    if (manualRetryTimer) {
        clearTimeout(manualRetryTimer)
        manualRetryTimer = null
    }
    isExecuting = true
    pendingAttrs = null
    const handlers = pendingHandlers
    pendingHandlers = []

    currentLocalParticipant!
        .setAttributes(attrsToSet)
        .then(() => handlers.forEach(({ resolve }) => resolve()))
        .catch((error) => handlers.forEach(({ reject }) => reject(error)))
        .finally(() => {
            isExecuting = false
            // Le passage de queue a pu tomber pendant l'écriture : ce qui attendait part maintenant, sinon il
            // restait là jusqu'au geste suivant.
            if (pendingAttrs) throttledFlush()
        })
}

// Module-level throttle: leading & trailing
const throttledFlush = throttle(executeFlush, DEFAULT_INTERVAL, { leading: true, trailing: true })

/**
 * Shared setter: merges attrs, queues handlers, and triggers throttle
 */
export function setThrottledAttributes(attrs: Record<string, string>): Promise<void> {
    // Guard: ensure local participant is initialized
    if (!currentLocalParticipant) {
        return Promise.reject(new Error("Local participant not initialized"))
    }
    return new Promise<void>((resolve, reject) => {
        pendingAttrs = { ...(pendingAttrs || {}), ...attrs }
        pendingHandlers.push({ resolve, reject })
        throttledFlush()
    })
}

/**
 * Hook that registers the current local participant and returns the shared setter.
 */
const useThrottledSetAttributes = (): ((attrs: Record<string, string>) => Promise<void>) => {
    const room = useRoomContext()
    const connectionState = useConnectionState()
    const connected = connectionState?.toString() === ConnectionState.Connected
    // Keep module-level participant and connection status in sync
    useEffect(() => {
        currentLocalParticipant = room.localParticipant as unknown as AttributesParticipant
    }, [room.localParticipant])
    useEffect(() => {
        isRoomConnected = connected
    }, [connected])
    return setThrottledAttributes
}

export default useThrottledSetAttributes
